import { useMemo, useContext, useEffect, useRef } from "react";

import FilterContext, { SelectedFilters } from "../contexts/FilterContext";
import Shape from "../../../models/furniture/Shape";
import Material from "../../../models/furniture/Material";

const DEFAULT_OPTIONS: (Shape | Material)[] = [];

function isShape(option: Shape | Material): option is Shape {
  return "dimension_tags" in option && "fabrics" in option;
}

function isMaterial(option: Shape | Material): option is Material {
  return "colors" in option && "material_subtype" in option;
}

function useFilter(optionsParam: (Shape | Material)[] = DEFAULT_OPTIONS): {
  filteredOptions: (Shape | Material)[];
} {
  const context = useContext(FilterContext);
  const prevOptionsRef = useRef<(Shape | Material)[]>([]);

  if (!context) {
    throw new Error("useFilter must be used within a FilterProvider");
  }

  const { setFilters, selectedFilters, resetSelectedFilters } = context;

  const options = useMemo(() => {
    return optionsParam.length === 0 ? DEFAULT_OPTIONS : optionsParam;
  }, [optionsParam]);

  useEffect(() => {
    const isSameOptions =
      options === prevOptionsRef.current ||
      (options.length === prevOptionsRef.current.length &&
        options.length > 0 &&
        prevOptionsRef.current.length > 0 &&
        options[0] === prevOptionsRef.current[0] &&
        options[options.length - 1] ===
          prevOptionsRef.current[prevOptionsRef.current.length - 1]);

    if (isSameOptions) {
      return;
    }

    prevOptionsRef.current = options;

    resetSelectedFilters();

    if (options.length === 0) {
      setFilters({
        colorsFilters: [],
        materialsFilters: [],
        stylesFilters: [],
        dimensionsFilters: [],
      });
      return;
    }

    const colorsFiltersSet = new Set<string>();
    const materialsFiltersSet = new Set<string>();
    const stylesFiltersSet = new Set<string>();
    const dimensionsFiltersSet = new Set<string>();

    options.forEach((option) => {
      if (isMaterial(option)) {
        option.colors?.forEach((color) => {
          colorsFiltersSet.add(color);
        });
      }

      if (isShape(option)) {
        option.fabrics?.forEach((fabric) => {
          materialsFiltersSet.add(fabric);
        });
      }

      option.styles?.forEach((style) => {
        stylesFiltersSet.add(style);
      });

      if (isShape(option)) {
        option.dimension_tags?.forEach((dimension) => {
          dimensionsFiltersSet.add(dimension);
        });
      }
    });

    const colorsFilters = Array.from(colorsFiltersSet).sort();
    const materialsFilters = Array.from(materialsFiltersSet).sort();
    const stylesFilters = Array.from(stylesFiltersSet).sort();
    const dimensionsFilters = Array.from(dimensionsFiltersSet).sort();

    setFilters({
      colorsFilters,
      materialsFilters,
      stylesFilters,
      dimensionsFilters,
    });
  }, [options, setFilters, resetSelectedFilters]);

  const filteredOptions = useMemo(() => {
    return filterOptions(options, selectedFilters);
  }, [selectedFilters, options]);

  return {
    filteredOptions,
  };
}

export default useFilter;

function filterOptions(
  options: (Shape | Material)[] = [],
  selectedFilters: SelectedFilters
): (Shape | Material)[] {
  const {
    selectedColorsFilters,
    selectedMaterialsFilters,
    selectedStylesFilters,
    selectedDimensionsFilters,
  } = selectedFilters;

  if (
    selectedColorsFilters.length === 0 &&
    selectedMaterialsFilters.length === 0 &&
    selectedStylesFilters.length === 0 &&
    selectedDimensionsFilters.length === 0
  ) {
    return options;
  }

  const colorPatterns = selectedColorsFilters.map(
    (filter) => new RegExp(filter, "i")
  );
  const materialPatterns = selectedMaterialsFilters.map(
    (filter) => new RegExp(filter, "i")
  );
  const stylePatterns = selectedStylesFilters.map(
    (filter) => new RegExp(filter, "i")
  );
  const dimensionPatterns = selectedDimensionsFilters.map(
    (filter) => new RegExp(filter, "i")
  );

  const filteredOptions = options.filter((option: Shape | Material) => {
    const hasMatchingColors =
      colorPatterns.length === 0 ||
      (isMaterial(option) &&
        colorPatterns.some((pattern) =>
          option.colors?.some((color: string) => pattern.test(color))
        )) ||
      !isMaterial(option);

    const hasMatchingMaterials =
      materialPatterns.length === 0 ||
      (isShape(option) &&
        materialPatterns.some((pattern) =>
          option.fabrics?.some((fabric: string) => pattern.test(fabric))
        )) ||
      !isShape(option);

    const hasMatchingStyles =
      stylePatterns.length === 0 ||
      stylePatterns.some((pattern) =>
        option.styles?.some((style: string) => pattern.test(style))
      );

    const hasMatchingDimensions =
      dimensionPatterns.length === 0 ||
      (isShape(option) &&
        dimensionPatterns.some((pattern) =>
          option.dimension_tags?.some((dimension: string) =>
            pattern.test(dimension)
          )
        )) ||
      !isShape(option);

    return (
      hasMatchingColors &&
      hasMatchingMaterials &&
      hasMatchingStyles &&
      hasMatchingDimensions
    );
  });

  return filteredOptions;
}
